import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { AccountForm } from "@/components/app/AccountForm";
import { PropertyFields, emptyProperty } from "@/components/app/PropertyFields";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function NewWorkspace() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [prop, setProp] = useState(emptyProperty());

  useEffect(() => { api.get("/workspaces").then(({ data }) => setWorkspaces(data.workspaces)).catch(() => {}); }, []);
  const sample = workspaces.find((w) => w.is_sample);
  const hasReal = workspaces.some((w) => !w.is_sample);

  const create = async (form) => {
    if (!prop.state) throw { response: { data: { detail: "Please choose the property's state." } } };
    const { data } = await api.post("/workspaces", { ...form, first_property: prop });
    toast.success("Workspace created");
    navigate(`/app/w/${data.workspace.id}`, { replace: true });
  };

  return (
    <div className="min-h-screen bg-paper">
      <header className="flex items-center justify-between border-b bg-white px-5 py-4 sm:px-10">
        <Logo to="/app" />
        <button onClick={logout} data-testid="onboarding-signout-button" className="text-sm text-slate-500 hover:text-navy">Sign out</button>
      </header>
      <main className="mx-auto grid max-w-5xl gap-10 px-5 py-12 sm:px-10 lg:grid-cols-[1fr_1.2fr]">
        <section className="fade-up">
          <p className="eyebrow text-gold-dark">{hasReal ? "Add a workspace" : "Step 2 of 2"}</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-navy">Set up your workspace</h1>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Tell us whether you're a homeowner or a real estate agent, then add your first property. You can add more properties later. Each workspace is private to its own members.
          </p>
          {sample && (
            <div className="mt-8 rounded-lg border border-dashed border-gold bg-gold-light/40 p-5">
              <p className="font-medium text-navy">Want to look around first?</p>
              <p className="mt-1 text-sm text-slate-600">The sample workspace has practice records. Nothing in it is real.</p>
              <Link to={`/app/w/${sample.id}`} data-testid="onboarding-open-sample-link" className="mt-3 inline-block text-sm font-medium text-navy underline underline-offset-4">
                Open the sample workspace
              </Link>
            </div>
          )}
        </section>
        <section className="rounded-xl border bg-white p-6 sm:p-8 shadow-sm fade-up" style={{ animationDelay: "120ms" }}>
          <AccountForm onSubmit={create} submitLabel="Create workspace">
            <div className="border-t pt-5">
              <p className="mb-4 font-display text-base font-semibold text-navy">Your first property or project</p>
              <PropertyFields value={prop} onChange={setProp} />
            </div>
          </AccountForm>
        </section>
      </main>
    </div>
  );
}
